import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  message, 
  Modal, 
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Tag,
  Typography,
  Upload,
  Image,
  Switch
} from 'antd';
import type { UploadProps } from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  UploadOutlined,
  LoadingOutlined,
  PercentageOutlined
} from '@ant-design/icons';
import { packageAPI } from '../../utils/api';
import { Package } from '../../types';

const { Title } = Typography;
const { TextArea } = Input;

// Get API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL;

interface PackageFormData {
  name: string;
  description: string;
  destination: string;
  price: number;
  discount?: number;
  showPrice?: boolean;
  includes?: string;
  image?: string;
}

interface PackagesManagementProps {
  active: string;
}

const PackagesManagement: React.FC<PackagesManagementProps> = ({ active }) => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);
  const [form] = Form.useForm<PackageFormData>();
  const hasFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (active === 'packages' && !hasFetchedRef.current) {
      fetchPackages();
      hasFetchedRef.current = true;
    }
  }, [active]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await packageAPI.getAllAdmin();
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Error fetching packages:', error);
      message.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPackage(null);
    setImageUrl('');
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (pkg: Package) => {
    setEditingPackage(pkg);
    setImageUrl(pkg.image || '');
    form.setFieldsValue({
      name: pkg.name,
      description: pkg.description,
      destination: pkg.destination,
      price: pkg.price,
      discount: pkg.discount || 0,
      showPrice: pkg.showPrice !== undefined ? pkg.showPrice : true,
      includes: pkg.includes ? pkg.includes.join('\n') : ''
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await packageAPI.delete(id);
      message.success('Package deleted successfully');
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      message.error('Failed to delete package');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await packageAPI.toggle(id);
      message.success('Package visibility updated');
      fetchPackages();
    } catch (error) {
      console.error('Error toggling package:', error);
      message.error('Failed to update package');
    }
  };

  const handleTogglePrice = async (id: string, showPrice: boolean) => {
    try {
      await packageAPI.update(id, { showPrice });
      message.success(`Price ${showPrice ? 'shown' : 'hidden'} successfully`);
      fetchPackages();
    } catch (error) {
      console.error('Error toggling price:', error);
      message.error('Failed to update price visibility');
    }
  };

  // Image upload handler - Works with BOTH Local and Cloudinary!
  const handleImageUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      
      // Upload to backend (works for both local and Cloudinary)
      const response = await fetch(`${API_URL}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      // Check if response is OK (status 200-299)
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${response.statusText}. ${errorText}`);
      }

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('Non-JSON response received:', responseText);
        throw new Error('Server returned non-JSON response. Please check if the upload endpoint is configured correctly.');
      }

      const data = await response.json();

      if (data.success) {
        // Handle both local URLs and Cloudinary URLs
        let uploadedImageUrl = data.imageUrl;
        
        // If it's a local URL (starts with /uploads), add the base URL
        if (!uploadedImageUrl.startsWith('http')) {
          uploadedImageUrl = `${API_URL}${uploadedImageUrl}`;
        }
        // If it's a Cloudinary URL, use it as-is (already has https://)
        
        setImageUrl(uploadedImageUrl);
        form.setFieldsValue({ image: uploadedImageUrl });
        message.success('Image uploaded successfully');
        onSuccess?.(data, file as any);
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      message.error(error.message || 'Failed to upload image');
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const handleImageRemove = () => {
    setImageUrl('');
    form.setFieldsValue({ image: '' });
  };

  const handleSubmit = async (values: PackageFormData) => {
    try {
      const packageData = {
        ...values,
        duration: '1 Day', // Auto-set for one-day trips
        image: imageUrl || values.image || '',
        discount: values.discount || 0,
        showPrice: values.showPrice !== undefined ? values.showPrice : true,
        includes: values.includes ? values.includes.split('\n').filter(item => item.trim()) : []
      };

      if (editingPackage) {
        await packageAPI.update(editingPackage._id, packageData);
        message.success('Package updated successfully');
      } else {
        await packageAPI.create(packageData);
        message.success('Package created successfully');
      }

      setModalVisible(false);
      setImageUrl('');
      form.resetFields();
      fetchPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      message.error('Failed to save package');
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (image: string) => (
        image ? (
          <Image
            src={image}
            alt="Package"
            width={80}
            height={60}
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
        ) : (
          <div style={{ 
            width: 80, 
            height: 60, 
            background: '#f0f0f0', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            borderRadius: '4px'
          }}>
            No Image
          </div>
        )
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      key: 'destination',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: Package) => (
        <div>
          {record.hasDiscount && record.discount > 0 ? (
            <div>
              <div style={{ 
                textDecoration: 'line-through', 
                color: '#999', 
                fontSize: '12px' 
              }}>
                ${price}
              </div>
              <div style={{ color: '#f5222d', fontWeight: 'bold' }}>
                ${record.discountedPrice}
              </div>
            </div>
          ) : (
            <span>${price}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      width: 100,
      render: (discount: number) => (
        discount > 0 ? (
          <Tag color="green">{discount}% OFF</Tag>
        ) : (
          <span>-</span>
        )
      ),
    },
    {
      title: 'Price Display',
      dataIndex: 'showPrice',
      key: 'showPrice',
      width: 150,
      render: (showPrice: boolean, record: Package) => (
        <Switch
          checked={showPrice}
          onChange={(checked) => handleTogglePrice(record._id, checked)}
          checkedChildren="Visible"
          unCheckedChildren="Hidden"
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'VISIBLE' : 'HIDDEN'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Package) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={record.isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => handleToggle(record._id)}
          >
            {record.isActive ? 'Hide' : 'Show'}
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this package?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={3} style={{ margin: 0 }}>Packages Management - One-Day Trips</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Add Package
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={packages}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingPackage ? 'Edit Package' : 'Add New Package'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setImageUrl('');
          form.resetFields();
        }}
        footer={null}
        width="95%"
        style={{ maxWidth: 700 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Image Upload Section */}
          <Form.Item label="Package Image">
            <div style={{ marginBottom: 16 }}>
              {imageUrl ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Image
                    src={imageUrl}
                    alt="Package preview"
                    width={200}
                    height={150}
                    style={{ objectFit: 'cover', borderRadius: '8px' }}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={handleImageRemove}
                    style={{ 
                      position: 'absolute', 
                      top: 8, 
                      right: 8 
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <Upload
                  name="image"
                  listType="picture-card"
                  showUploadList={false}
                  customRequest={handleImageUpload}
                  accept="image/*"
                  disabled={uploading}
                >
                  <div>
                    {uploading ? <LoadingOutlined /> : <UploadOutlined />}
                    <div style={{ marginTop: 8 }}>
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </div>
                  </div>
                </Upload>
              )}
            </div>
            <p style={{ color: '#666', fontSize: '12px' }}>
              Recommended: 1200x800px. Max: 10MB. Formats: JPG, PNG, GIF, WebP
            </p>
          </Form.Item>

          <Form.Item
            name="image"
            label="Or Enter Image URL"
            help="You can also paste an external image URL"
          >
            <Input 
              placeholder="https://example.com/image.jpg"
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Package Name"
            rules={[{ required: true, message: 'Please enter package name' }]}
          >
            <Input placeholder="e.g., Cultural Triangle Explorer" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Describe the tour package..." 
            />
          </Form.Item>

          <Form.Item
            name="destination"
            label="Destination"
            rules={[{ required: true, message: 'Please enter destination' }]}
          >
            <Input placeholder="e.g., Kandy, Galle, Ella" />
          </Form.Item>

          {/* Duration is auto-set to "1 Day" - no need for input field */}
          <div style={{ 
            padding: '12px', 
            background: '#f0f9ff', 
            border: '1px solid #91d5ff',
            borderRadius: '4px',
            marginBottom: '24px'
          }}>
            <Typography.Text type="secondary">
              ℹ️ Duration is automatically set to <strong>"1 Day"</strong> for all packages (one-day trips)
            </Typography.Text>
          </div>

          <Form.Item
            name="price"
            label="Price per Person"
            rules={[{ required: true, message: 'Please enter price' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              prefix="$"
              placeholder="e.g., 999"
            />
          </Form.Item>

          <Form.Item
            name="discount"
            label="Discount Percentage"
            help="Set a percentage discount (0-100%). Leave at 0 for no discount."
          >
            <InputNumber<number>
              style={{ width: '100%' }}
              min={0}
              max={100}
              prefix={<PercentageOutlined />}
              placeholder="e.g., 20"
              formatter={value => `${value}%`}
              parser={value => {
                  const parsed = value?.replace('%', '');
                  return parsed ? Number(parsed) : 0;
                }}
            />
          </Form.Item>

          <Form.Item
            name="showPrice"
            label="Show Price to Customers"
            valuePropName="checked"
            initialValue={true}
            help="When disabled, customers will see 'Contact for Price' instead of the actual price"
          >
            <Switch
              checkedChildren="Visible"
              unCheckedChildren="Hidden"
            />
          </Form.Item>

          <Form.Item
            name="includes"
            label="Package Includes (one per line)"
          >
            <TextArea
              rows={5}
              placeholder="e.g.,
                            Hotel accommodation
                            Daily breakfast
                            Airport transfers
                            Tour guide
                            Entrance fees"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={uploading}>
                {editingPackage ? 'Update' : 'Create'} Package
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setImageUrl('');
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PackagesManagement;